/**
 * CheckoutViewModel
 * Platform-agnostic checkout functionality with comprehensive payment and order management
 * Manages driver tips, payment methods, order summary, and order placement
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import type { IPlatformServices } from '../contracts'
import { safeGetLogger } from '@restaurant/logging'

import type {
  CheckoutViewModelDependencies,
  CheckoutViewModel,
  CheckoutViewModelFactory,
  PaymentMethodOption,
  SelectedTip,
  CustomTipState,
  OrderSummaryData,
} from './CheckoutViewModel.types'
import type { SectionCheckoutDriverTipProps } from '@restaurant/core/components/SectionCheckoutDriverTip/SectionCheckoutDriverTip.types'
import type { SectionCheckoutPaymentMethodProps } from '@restaurant/core/components/SectionCheckoutPaymentMethod/SectionCheckoutPaymentMethod.types'
import type { SectionOrderSummaryProps } from '@restaurant/core/components/SectionOrderSummary/SectionOrderSummary.types'
import type { CustomTipState as ComponentCustomTipState } from '@restaurant/core/components/CustomTipOption/CustomTipOption.types'

/**
 * Map ViewModel CustomTipState ('inactive' | 'active' | 'submitting')
 * to component CustomTipState ('Default' | 'Active' | 'Filled')
 */
const mapCustomTipState = (vmState: CustomTipState): ComponentCustomTipState => {
  switch (vmState) {
    case 'active':
      return 'Active'
    case 'submitting':
      return 'Filled'
    case 'inactive':
    default:
      return 'Default'
  }
}

/**
 * Create platform-agnostic CheckoutViewModel
 * Uses dependency injection for checkout service
 */
export const createCheckoutViewModel = (
  services: IPlatformServices,
  dependencies?: CheckoutViewModelDependencies
): CheckoutViewModelFactory => {
  return (): CheckoutViewModel => {
    // Loading states
    const [loading, setLoading] = useState(true)
    const [placingOrder, setPlacingOrder] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // Payment method state
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethodOption[]>([])
    const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | null>(null)
    // Live mirror of selectedPaymentMethodId for async refreshes: a refresh's
    // getPaymentMethods can be in flight while the user selects a method
    // (CartPage awaits refreshCheckoutData AFTER opening the sheet). Reading
    // this ref when the refresh resolves lets it preserve that selection
    // instead of stomping it with the service's selected:true saved card
    // (2026-07-15 wallet pilot bug: wallet radio selection kept reverting).
    const selectedPaymentMethodIdRef = useRef<string | null>(null)
    selectedPaymentMethodIdRef.current = selectedPaymentMethodId

    // Tip state
    const [selectedTip, setSelectedTip] = useState<SelectedTip | null>(null)
    const [customTipAmount, setCustomTipAmount] = useState('')
    const [customTipState, setCustomTipState] = useState<CustomTipState>('inactive')

    // Order summary state
    const [orderSummary, setOrderSummary] = useState<OrderSummaryData | null>(null)

    // Validation state
    const [canPlaceOrder, setCanPlaceOrder] = useState(false)
    const canPlaceOrderRef = useRef(canPlaceOrder)
    canPlaceOrderRef.current = canPlaceOrder

    // Closed location alert state
    const [closedLocationAlert, setClosedLocationAlert] = useState<{ visible: boolean; hoursText: string } | null>(null)

    // Get checkout service from dependencies
    const checkoutService = dependencies?.checkoutService

    // Initialize checkout data
    useEffect(() => {
      const initializeCheckout = async () => {
        if (!checkoutService) {
          setError('Checkout service not available')
          setLoading(false)
          return
        }

        try {
          setLoading(true)
          setError(null)

          // Load payment methods and order summary in parallel
          const [paymentMethodsResult, orderSummaryResult] = await Promise.all([
            checkoutService.getPaymentMethods(),
            checkoutService.getOrderSummary(),
          ])

          setPaymentMethods(paymentMethodsResult)
          setOrderSummary(orderSummaryResult)

          // Auto-select the first pre-selected payment method.
          // Persist via the service (web → sessionStorage, native → AsyncStorage)
          // BEFORE setting React state so that when the validation effect fires
          // (triggered by the state change), the persisted value is available
          // for validateCheckout(). Awaiting here is safe — the finally block
          // still runs and clears loading.
          const selectedMethod = paymentMethodsResult.find(method => method.selected)
          if (selectedMethod) {
            await checkoutService.selectPaymentMethod(selectedMethod)
            setSelectedPaymentMethodId(selectedMethod.id)
          }

        } catch (err) {
          safeGetLogger()?.error('Checkout init failed', { domain: 'checkout', error: String(err) })
          setError('Failed to load checkout data')
        } finally {
          setLoading(false)
        }
      }

      initializeCheckout()
    }, [checkoutService])

    // Validate checkout when payment method or tip changes
    useEffect(() => {
      const validateCheckout = async () => {
        if (!checkoutService) return

        try {
          const validation = await checkoutService.validateCheckout()
          setCanPlaceOrder(validation.isValid)
        } catch (err) {
          safeGetLogger()?.error('Checkout validation failed', { domain: 'checkout', error: String(err) })
          setCanPlaceOrder(false)
        }
      }

      if (!loading && selectedPaymentMethodId) {
        validateCheckout()
      } else if (!loading) {
        // No payment method selected — skip validation and immediately disable
        setCanPlaceOrder(false)
      }
    }, [checkoutService, loading, selectedPaymentMethodId, selectedTip])

    // Update order summary when tip changes
    useEffect(() => {
      const updateOrderSummary = async () => {
        if (!checkoutService || loading) return

        try {
          const updatedSummary = await checkoutService.getOrderSummary()
          setOrderSummary(updatedSummary)
        } catch (err) {
          safeGetLogger()?.error('Order summary update failed', { domain: 'checkout', error: String(err) })
        }
      }

      updateOrderSummary()
    }, [checkoutService, selectedTip, loading])

    // Payment method actions
    const selectPaymentMethod = useCallback(async (paymentMethod: PaymentMethodOption) => {
      if (!checkoutService) return

      try {
        setError(null)
        await checkoutService.selectPaymentMethod(paymentMethod)
        // Update the ref immediately (not just via the render-time mirror) so
        // an already-in-flight refresh resolving before the re-render commits
        // still sees this selection.
        selectedPaymentMethodIdRef.current = paymentMethod.id
        setSelectedPaymentMethodId(paymentMethod.id)

        // Update payment methods list to reflect selection
        setPaymentMethods(prev => 
          prev.map(method => ({
            ...method,
            selected: method.id === paymentMethod.id
          }))
        )
      } catch (err) {
        safeGetLogger()?.error('Payment method selection failed', { domain: 'checkout', error: String(err) })
        setError('Failed to select payment method')
      }
    }, [checkoutService])

    const deletePaymentMethod = useCallback(async (paymentMethod: PaymentMethodOption) => {
      if (!checkoutService) return

      try {
        setError(null)
        await checkoutService.deletePaymentMethod(paymentMethod)

        // Reload payment methods after deletion
        const updatedMethods = await checkoutService.getPaymentMethods()
        setPaymentMethods(updatedMethods)

        // Clear selection if deleted method was selected
        if (selectedPaymentMethodId === paymentMethod.id) {
          setSelectedPaymentMethodId(null)
        }
      } catch (err) {
        safeGetLogger()?.error('Payment method deletion failed', { domain: 'checkout', error: String(err) })
        setError('Failed to delete payment method')
      }
    }, [checkoutService, selectedPaymentMethodId])

    const onUpdateCardPress = useCallback(() => {
      dependencies?.onAddCardPress?.()
    }, [dependencies?.onAddCardPress])

    const onAddCard = useCallback(() => {
      dependencies?.onAddCardPress?.()
    }, [dependencies?.onAddCardPress])

    const refreshPaymentMethods = useCallback(async () => {
      if (!checkoutService) return
      try {
        const methods = await checkoutService.getPaymentMethods()

        // Preserve a selection the user made (possibly while this refresh was
        // in flight) — only auto-select from the service's selected flags when
        // there is no current selection or it no longer exists in the list.
        const current = selectedPaymentMethodIdRef.current
        if (current && methods.some(method => method.id === current)) {
          setPaymentMethods(methods.map(method => ({ ...method, selected: method.id === current })))
          return
        }

        setPaymentMethods(methods)
        // Auto-select the selected method so validation re-runs. Persist via
        // the service to keep web + native in sync (same rationale as init).
        const selectedMethod = methods.find(method => method.selected)
        if (selectedMethod) {
          await checkoutService.selectPaymentMethod(selectedMethod)
          selectedPaymentMethodIdRef.current = selectedMethod.id
          setSelectedPaymentMethodId(selectedMethod.id)
        }
      } catch (err) {
        safeGetLogger()?.error('Payment method refresh failed', { domain: 'checkout', error: String(err) })
      }
    }, [checkoutService])

    // Tip actions
    const selectTip = useCallback((selectedTipData: SelectedTip | null) => {
      setSelectedTip(selectedTipData)
      
      // Clear custom tip state when selecting preset tip
      if (selectedTipData?.selectedType === 'preset') {
        setCustomTipState('inactive')
        setCustomTipAmount('')
      }
    }, [])

    const activateCustomTip = useCallback(() => {
      setCustomTipState('active')
      setSelectedTip(null) // Clear preset tip selection
    }, [])

    const updateCustomTipAmount = useCallback((amount: string) => {
      setCustomTipAmount(amount)
      
      if (checkoutService && amount) {
        checkoutService.setCustomTip(amount)
      }
    }, [checkoutService])

    const submitCustomTip = useCallback((amount: string) => {
      const numericAmount = parseFloat(amount)
      if (!isNaN(numericAmount) && numericAmount >= 0) {
        setSelectedTip({
          percentage: null,
          amount: numericAmount,
          selectedType: 'custom'
        })
        setCustomTipState('inactive')
      }
    }, [])

    const clearCustomTip = useCallback(() => {
      setCustomTipAmount('')
      setCustomTipState('inactive')
      if (checkoutService) {
        checkoutService.setCustomTip('0')
      }
      setSelectedTip(null)
    }, [checkoutService])

    // Order actions
    const placeOrder = useCallback(async () => {
      // Gate: check if location is currently accepting orders
      if (checkoutService) {
        const availability = checkoutService.checkLocationAcceptingOrders()
        if (!availability.available) {
          setClosedLocationAlert({ visible: true, hoursText: availability.hoursText })
          return
        }
      }

      if (!checkoutService || !canPlaceOrderRef.current) return

      if (placingOrder) {
        // Guard against concurrent calls — inform the user their tap was received
        services.platformService.showToast('Your order is already being processed')
        return
      }

      try {
        setPlacingOrder(true)
        setError(null)
        const orderResult = await checkoutService.placeOrder()

        // Notify parent (e.g. CartPage transitions sheet to order-tracking mode)
        if (dependencies?.onOrderPlaced) {
          dependencies.onOrderPlaced(orderResult)
        } else {
          // Fallback: navigate to order confirmation
          services.navigationService.navigate('/order-confirmation', {
            orderId: orderResult.id
          })
        }
      } catch (err: any) {
        safeGetLogger()?.error('Order placement failed', { domain: 'order', error: String(err) })

        if (err?.type === 'payment_declined' && err?.errorType === 'card_removed') {
          // Card was removed by the server — clear selection and reload the list
          setSelectedPaymentMethodId(null)
          try {
            const reloadedMethods = await checkoutService.getPaymentMethods()
            setPaymentMethods(reloadedMethods)
          } catch (reloadErr) {
            safeGetLogger()?.error('Payment methods reload failed', { domain: 'checkout', error: String(reloadErr) })
          }
          // Show the server-provided actionable message inline (no toast — payment section is visible)
          setError(err.details ?? 'Your payment method was removed. Please add a new card.')
        } else if (err?.type === 'payment_declined') {
          // Other 402 declines — show server details inline, no toast
          setError(err.details ?? 'Your payment was declined. Please try a different card.')
        } else if (err?.code === 'pos_auth_required') {
          // Merchant's POS (Clover) token is revoked — the payment system is
          // offline for this restaurant. Show the actionable message inline (no
          // toast — the payment section is visible); like the 402 decline branch,
          // this stays inline (no toast).
          setError(
            err.message ??
              "This restaurant's payment system is temporarily offline. Please try again later or let the restaurant know.",
          )
        } else if (err?.code === 'upstream_rejected') {
          // Clover rejected the order payload (502 + details). Deterministic —
          // show the reason inline, no toast, so the user fixes the cart
          // instead of retrying an identical order.
          setError(
            err?.details
              ? `The restaurant's ordering system rejected this order: ${err.details}`
              : err?.message ?? "The restaurant's ordering system rejected this order. Please review your cart and try again.",
          )
        } else if (err?.type === 'serverError' || err?.type === 'network') {
          // Server or network failures — show a toast (sheet error UI may not be prominent)
          services.platformService.showToast({
            message: 'Something went wrong. Please try again.',
            type: 'error',
          })
          setError('Something went wrong. Please try again.')
        } else if (err?.type === 'cart_not_synced') {
          // The draft order was never created (cart sync failed earlier) and
          // re-creating it at checkout also failed. Surface a clear retry
          // message instead of the opaque "Order ID is required" error.
          const message = err?.message || "We couldn't save your cart, please retry."
          services.platformService.showToast({ message, type: 'error' })
          setError(message)
        } else {
          // Unknown / unexpected errors — still show a toast so the user sees feedback
          services.platformService.showToast({
            message: 'Something went wrong. Please try again.',
            type: 'error',
          })
          setError('Failed to place order. Please try again.')
        }
      } finally {
        setPlacingOrder(false)
      }
    }, [checkoutService, placingOrder, services.navigationService, services.platformService])

    const clearError = useCallback(() => {
      setError(null)
    }, [])

    const dismissClosedAlert = useCallback(() => {
      setClosedLocationAlert(null)
    }, [])

    const onChangeLocation = useCallback(() => {
      setClosedLocationAlert(null)
      dependencies?.onChangeLocation?.()
    }, [dependencies?.onChangeLocation])

    // Lightweight refresh — re-fetches only the order summary, skips the loading
    // toggle and payment-methods fetch. Call from platform wrappers when cart
    // state changes so the sheet's line items stay in sync with the store without
    // flicker (gotcha #21 pattern — reactivity bridge for service-pull viewmodels).
    const refreshOrderSummary = useCallback(async () => {
      if (!checkoutService) return
      try {
        const summary = await checkoutService.getOrderSummary()
        setOrderSummary(summary)
      } catch (err) {
        safeGetLogger()?.error('Order summary refresh failed', { domain: 'checkout', error: String(err) })
      }
    }, [checkoutService])

    // Refresh checkout data (order summary + payment methods) from current store state.
    // Called when the checkout sheet opens so stale location / order-type data is replaced.
    const refreshCheckoutData = useCallback(async () => {
      if (!checkoutService) return

      try {
        setLoading(true)
        setError(null)

        const [paymentMethodsResult, orderSummaryResult] = await Promise.all([
          checkoutService.getPaymentMethods(),
          checkoutService.getOrderSummary(),
        ])

        setOrderSummary(orderSummaryResult)

        // Preserve a selection the user made (possibly while this refresh was
        // in flight — CartPage awaits this AFTER opening the sheet, so a tap
        // can land mid-flight; 2026-07-15 wallet pilot bug). Only auto-select
        // from the service's selected flags when there is no current selection
        // or it no longer exists in the refreshed list.
        const current = selectedPaymentMethodIdRef.current
        if (current && paymentMethodsResult.some(method => method.id === current)) {
          setPaymentMethods(paymentMethodsResult.map(method => ({ ...method, selected: method.id === current })))
        } else {
          setPaymentMethods(paymentMethodsResult)
          const selectedMethod = paymentMethodsResult.find(method => method.selected)
          if (selectedMethod) {
            selectedPaymentMethodIdRef.current = selectedMethod.id
            setSelectedPaymentMethodId(selectedMethod.id)
          }
        }
      } catch (err) {
        safeGetLogger()?.error('Checkout refresh failed', { domain: 'checkout', error: String(err) })
        setError('Failed to load checkout data')
      } finally {
        setLoading(false)
      }
    }, [checkoutService])

    // Component integration props
    const driverTipProps = useMemo((): Partial<SectionCheckoutDriverTipProps> => {
      const subtotal = orderSummary?.lineItems?.find(item => 
        item.label.toLowerCase().includes('subtotal')
      )?.amount
      
      // Convert subtotal to cents (remove $ and convert to cents)
      const subtotalCents = subtotal ? 
        Math.round(parseFloat(subtotal.replace('$', '')) * 100) : 
        2500 // Default $25.00

      return {
        subtotalCents,
        tipPercentages: [15, 18, 20] as const,
        selectedPercentage: selectedTip?.selectedType === 'preset' ? (selectedTip.percentage ?? undefined) : undefined,
        customTipAmount,
        customTipState: mapCustomTipState(customTipState),
        sectionName: 'Driver Tip',
        testID: 'driver-tip-section',
        onTipSelect: (selectedTipData) => {
          if (selectedTipData && checkoutService) {
            const calculatedTip = checkoutService.calculateTip(
              selectedTipData.percentage,
              'preset'
            )
            selectTip(calculatedTip)
          } else {
            if (checkoutService) {
              checkoutService.setCustomTip('0')
            }
            selectTip(null)
          }
        },
        onCustomTipActivate: activateCustomTip,
        onCustomTipChange: updateCustomTipAmount,
        onCustomTipSubmit: submitCustomTip,
        onCustomTipClear: clearCustomTip,
      }
    }, [
      orderSummary, 
      selectedTip, 
      customTipAmount, 
      customTipState, 
      checkoutService, 
      selectTip,
      activateCustomTip,
      updateCustomTipAmount,
      submitCustomTip,
      clearCustomTip
    ])

    const paymentMethodProps = useMemo((): Partial<SectionCheckoutPaymentMethodProps> => ({
      sectionName: 'Payment Method',
      testID: 'payment-method-section',
      paymentMethods,
      selectedPaymentMethodId: selectedPaymentMethodId ?? undefined,
      showApplePay: false,
      showGooglePay: false,
      showPayPal: false,
      showVenmo: false,
      showSavedCard: false,
      onPaymentMethodSelect: selectPaymentMethod,
      onPaymentMethodDelete: deletePaymentMethod,
      onUpdateCardPress,
      onAddCard,
    }), [
      paymentMethods,
      selectedPaymentMethodId,
      selectPaymentMethod,
      deletePaymentMethod,
      onUpdateCardPress,
      onAddCard,
    ])

    const orderSummaryProps = useMemo((): Partial<SectionOrderSummaryProps> => ({
      title: orderSummary?.title,
      locationAddress: orderSummary?.locationAddress,
      lineItems: orderSummary?.lineItems,
      total: orderSummary?.total,
      buttonText: 'Place Order',
      testID: 'order-summary',
      onPlaceOrder: placeOrder,
      loading: placingOrder,
      disabled: !canPlaceOrder || placingOrder,
      errorMessage: error,
    }), [
      orderSummary,
      placeOrder,
      placingOrder,
      canPlaceOrder,
      error,
    ])

    return {
      // State
      loading,
      placingOrder,
      error,
      paymentMethods,
      selectedPaymentMethodId,
      selectedTip,
      customTipAmount,
      customTipState,
      orderSummary,
      canPlaceOrder,
      closedLocationAlert,

      // Actions
      selectPaymentMethod,
      deletePaymentMethod,
      onUpdateCardPress,
      onAddCard,
      refreshPaymentMethods,
      refreshCheckoutData,
      refreshOrderSummary,
      selectTip,
      activateCustomTip,
      updateCustomTipAmount,
      submitCustomTip,
      clearCustomTip,
      placeOrder,
      clearError,
      dismissClosedAlert,
      onChangeLocation,

      // Component props
      driverTipProps,
      paymentMethodProps,
      orderSummaryProps,
    }
  }
}